import { useCallback, useState } from 'react';
import { AskArchiveError, AskArchiveResponse } from '../types/askArchive';

const defaultApiUrl = process.env.NODE_ENV === 'production'
  ? 'https://api.gibsonsleague.com/ask'
  : 'http://localhost:8787/ask';

const askApiUrl = process.env.REACT_APP_ASK_API_URL?.trim() || defaultApiUrl;

export const useAskArchive = () => {
  const [answer, setAnswer] = useState<AskArchiveResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ask = useCallback(async (question: string) => {
    setIsLoading(true);
    setError(null);
    setAnswer(null);

    try {
      const response = await fetch(askApiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ question }),
      });

      const payload = await response.json().catch(() => ({
        error: response.status === 404
          ? 'The Ask API has not been configured for this site yet.'
          : 'The Ask API returned an unreadable response.',
      })) as AskArchiveResponse | AskArchiveError;

      if (!response.ok) {
        throw new Error('error' in payload && payload.error
          ? payload.error
          : 'The archive could not answer that question.');
      }

      setAnswer(payload as AskArchiveResponse);
      return payload as AskArchiveResponse;
    } catch (requestError) {
      const message = requestError instanceof Error
        ? (requestError.message === 'Failed to fetch'
          ? 'The Ask API could not be reached. Please try again shortly.'
          : requestError.message)
        : 'The archive could not answer that question.';
      setError(message);
      throw requestError;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setAnswer(null);
    setError(null);
  }, []);

  return { ask, answer, error, isLoading, reset };
};
